import { DocumentData, Timestamp } from 'firebase/firestore';
import { FirestoreDocument, FirestoreField } from './types';

export function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (value instanceof Timestamp) {
    return value.toDate().toLocaleString();
  }
  if (typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate().toLocaleString();
  }
  if (typeof value === 'object' && 'path' in value) {
    return `ref:${(value as { path: string }).path}`;
  }
  if (Array.isArray(value)) {
    return `[${value.length} items]`;
  }
  if (typeof value === 'object') {
    return `{${Object.keys(value).length} fields}`;
  }
  return String(value);
}

export function getFieldType(value: unknown): FirestoreField['type'] {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (typeof value === 'string') {
    return 'string';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (value instanceof Timestamp || (typeof value === 'object' && 'toDate' in value)) {
    return 'timestamp';
  }
  if (typeof value === 'object' && 'path' in value) {
    return 'reference';
  }
  if (Array.isArray(value)) {
    return 'array';
  }
  if (typeof value === 'object') {
    return 'object';
  }
  return 'string';
}

export function inferFieldsFromDocuments(documents: FirestoreDocument[]): string[] {
  const fieldSet = new Set<string>();
  documents.forEach((doc) => {
    Object.keys(doc.data).forEach((key) => {
      fieldSet.add(key);
    });
  });
  return Array.from(fieldSet).sort();
}

export function validateJson(jsonString: string): {
  valid: boolean;
  error?: string;
  data?: unknown;
} {
  try {
    const parsed = JSON.parse(jsonString);
    return { valid: true, data: parsed };
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Invalid JSON',
    };
  }
}

export function parseFirestoreValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate().toISOString();
  }
  if (typeof value === 'object' && 'path' in value) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(parseFirestoreValue);
  }
  if (typeof value === 'object' && value !== null) {
    const parsed: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      parsed[key] = parseFirestoreValue(val);
    }
    return parsed;
  }
  return value;
}

export function convertToFirestoreData(data: unknown): DocumentData {
  if (typeof data !== 'object' || data === null) {
    return data as DocumentData;
  }
  if (Array.isArray(data)) {
    return data.map(convertToFirestoreData) as unknown as DocumentData;
  }
  const result: DocumentData = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      result[key] = new Date(value);
    } else if (typeof value === 'object' && value !== null) {
      result[key] = convertToFirestoreData(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Parses a collection path into base collection and subcollection path
 * @param path - Full collection path (e.g., "orders" or "orders/order1/items")
 * @returns Object with collection and optional subcollectionPath
 */
export function parseCollectionPath(path: string): {
  collection: string;
  subcollectionPath?: string;
  segments: Array<{ type: 'collection' | 'document' | 'subcollection'; name: string }>;
} {
  const parts = path.split('/');
  const segments: Array<{ type: 'collection' | 'document' | 'subcollection'; name: string }> = [];

  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      // Even indices are collections/subcollections
      segments.push({
        type: i === 0 ? 'collection' : 'subcollection',
        name: parts[i],
      });
    } else {
      // Odd indices are documents
      segments.push({
        type: 'document',
        name: parts[i],
      });
    }
  }

  if (parts.length === 1) {
    return {
      collection: parts[0],
      segments,
    };
  }

  // For subcollection paths, extract base collection and full path
  return {
    collection: parts[0],
    subcollectionPath: path,
    segments,
  };
}

/**
 * Builds a collection path from base collection and optional subcollection path
 * @param collection - Base collection name
 * @param subcollectionPath - Optional subcollection path (e.g., "order1/items")
 * @returns Full collection path
 */
export function buildCollectionPath(collection: string, subcollectionPath?: string): string {
  if (!subcollectionPath) {
    return collection;
  }
  return `${collection}/${subcollectionPath}`;
}

/**
 * Updates URL search parameters in the correct order: connectionId, collection, doc
 * @param currentParams - Current URLSearchParams
 * @param updates - Object with connectionId, collection, and/or doc to set
 *   - If a value is a string, it will be set
 *   - If a value is null, it will be removed
 *   - If a value is undefined, the existing value will be kept (if any)
 * @returns New URLSearchParams with parameters in the correct order
 */
export function updateUrlParams(
  currentParams: URLSearchParams,
  updates: {
    connectionId?: string | null;
    collection?: string | null;
    doc?: string | null;
  }
): URLSearchParams {
  const newParams = new URLSearchParams();

  if (updates.connectionId !== undefined) {
    if (updates.connectionId) {
      newParams.set('connectionId', updates.connectionId);
    }
  } else {
    const existing = currentParams.get('connectionId');
    if (existing) {
      newParams.set('connectionId', existing);
    }
  }

  if (updates.collection !== undefined) {
    if (updates.collection) {
      // URL-encode the collection path to handle subcollection paths
      newParams.set('collection', encodeURIComponent(updates.collection));
    }
  } else {
    const existing = currentParams.get('collection');
    if (existing) {
      newParams.set('collection', existing);
    }
  }

  if (updates.doc !== undefined) {
    if (updates.doc) {
      newParams.set('doc', updates.doc);
    }
  } else {
    const existing = currentParams.get('doc');
    if (existing) {
      newParams.set('doc', existing);
    }
  }

  return newParams;
}
